import { useNavigate } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '../components/feedback/toast';
import { Button } from '../components/ui/Button';
import { useMe, useSession } from './session';

// Idle sign-out (docs/architecture.md §9b). Policy comes from the API (`session.
// idleTimeoutMinutes`: strictest across the user's organizations, 15 min max for platform staff).
// Activity in any tab counts, shared through localStorage. A minute before the cut-off a
// countdown asks "Still there?"; at zero the session ends.

const ACTIVITY_KEY = 'clairo.activity';
const WARN_SECONDS = 60;
const EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

export function IdleGuard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { signOut } = useSession();
  const minutes = me?.session.idleTimeoutMinutes ?? 15;
  const lastActivity = useRef(Date.now());
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  const touch = useCallback(() => {
    const now = Date.now();
    // Throttle writes: once every 5 s is plenty to keep other tabs in sync.
    if (now - lastActivity.current > 5000) {
      try {
        localStorage.setItem(ACTIVITY_KEY, String(now));
      } catch {
        // Other tabs won't see it; this tab still tracks its own activity.
      }
    }
    lastActivity.current = now;
    setSecondsLeft(null);
  }, []);

  useEffect(() => {
    for (const e of EVENTS) window.addEventListener(e, touch, { passive: true });
    const onStorage = (e: StorageEvent) => {
      if (e.key === ACTIVITY_KEY && e.newValue) {
        lastActivity.current = Number(e.newValue);
        setSecondsLeft(null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      for (const e of EVENTS) window.removeEventListener(e, touch);
      window.removeEventListener('storage', onStorage);
    };
  }, [touch]);

  useEffect(() => {
    const timer = setInterval(() => {
      const idleSeconds = (Date.now() - lastActivity.current) / 1000;
      const remaining = Math.ceil(minutes * 60 - idleSeconds);
      if (remaining <= 0) {
        clearInterval(timer);
        void signOut().then(() => {
          toast.info(t('session.idle.signedOut', { minutes }));
          void navigate({ to: '/sign-in' });
        });
      } else if (remaining <= WARN_SECONDS) {
        setSecondsLeft(remaining);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [minutes, navigate, signOut, t]);

  const ratio = secondsLeft === null ? 1 : secondsLeft / WARN_SECONDS;
  const r = 34;
  const c = 2 * Math.PI * r;

  return (
    <AnimatePresence>
      {secondsLeft !== null && (
        <motion.div
          className="fixed inset-0 z-[70] grid place-items-center bg-black/45 px-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            role="alertdialog"
            aria-labelledby="idle-title"
            aria-describedby="idle-body"
            initial={{ scale: 0.94, y: 12 }}
            animate={{ scale: 1, y: 0 }}
            className="w-full max-w-sm rounded-xl border border-border bg-surface p-7 text-center elevated"
          >
            <svg
              viewBox="0 0 80 80"
              className="mx-auto mb-4 h-20 w-20 -rotate-90"
              aria-hidden="true"
            >
              <circle cx="40" cy="40" r={r} fill="none" stroke="var(--subtle)" strokeWidth="6" />
              <motion.circle
                cx="40"
                cy="40"
                r={r}
                fill="none"
                stroke="var(--warning)"
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={c}
                animate={{ strokeDashoffset: c * (1 - ratio) }}
                transition={{ duration: 1, ease: 'linear' }}
              />
              <text
                x="40"
                y="40"
                textAnchor="middle"
                dominantBaseline="central"
                transform="rotate(90 40 40)"
                className="tabular fill-text font-display text-xl font-bold"
              >
                {secondsLeft}
              </text>
            </svg>
            <h2 id="idle-title" className="text-xl font-bold text-text">
              {t('session.idle.title')}
            </h2>
            <p id="idle-body" className="mt-2 text-base text-muted">
              {t('session.idle.body', { seconds: secondsLeft })}
            </p>
            <Button size="lg" className="mt-6 w-full" onClick={touch} autoFocus>
              {t('session.idle.stay')}
            </Button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
